import { convertTimeString, getStoreAddressString } from "@/apiUtils/commonUtils";
import ChevronDownIcon from "@/assets/icons/chevron-down.svg?react";
import RTAEyeTest from "@/assets/icons/rta-eye-test.svg?react";
import BookingToastView from "@/common/components/BookingToastView";
import IconCalendar from "@/common/core/icons/IconCalendar";
import IconClock from "@/common/core/icons/IconClock";
import IconLocation from "@/common/core/icons/IconLocation";
import {
  CalendarEventDetails,
  StoreType,
  SuccessSectionContent,
  TypeToastMessage,
} from "@/common/lib/types";
import Accordion from "@/components/ui/accordion";
import Button from "@/components/ui/button/Button";
import { CLink } from "@/components/common/customLink/CustomLink";
import { Dispatch, FC, ReactNode, SetStateAction, useState } from "react";
import AddToCalendarModal from "./components/AddToCalendarModal";
import CancelConfirmationModal from "./components/CancelConfirmationModal";

import RenderCurrencyInText from "@/components/common/RenderCurrencyInText";

interface EyeTestSuccessViewProps {
  bookingId?: string;
  locale?: string;
  storeDetails?: StoreType;
  serviceTitle?: string;
  icon?: ReactNode;
  slotDate: string;
  startTime?: string;
  endTime?: string;
  content?: SuccessSectionContent;
  eventDetails: CalendarEventDetails;
  bookingToast: TypeToastMessage;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  handleCancelBooking: (reason: string) => void;
  closeBookingToast: () => void;
}

const EyeTestSuccessView: FC<EyeTestSuccessViewProps> = (props) => {
  const {
    bookingId,
    locale,
    slotDate,
    startTime,
    endTime,
    storeDetails,
    serviceTitle,
    icon,
    content,
    eventDetails,
    bookingToast,
    isModalOpen,
    setIsModalOpen,
    handleCancelBooking,
    closeBookingToast,
  } = props;

  const [isAddToCalendarModalOpen, setIsAddToCalendarModalOpen] = useState<boolean>(false);

  return (
    <>
      <BookingToastView
        type={bookingToast?.type}
        open={bookingToast?.open}
        message={bookingToast?.message}
        toggle={() => closeBookingToast()}
        icon={icon}
      />
      <div
        className="alert alert-success mb-7"
        style={{ backgroundColor: "#F1EBE3", borderRadius: "0" }}
      >
        <RenderCurrencyInText 
          text={content?.reservationSuccessMessage} 
          className="text-[0.875rem] font-normal" 
        />
      </div>
      <Accordion
        expandIconRotation="rotate-180"
        headerClasses="border-none"
        expandIcon={<ChevronDownIcon strokeWidth={3} height={7} width={7} />}
        openIndices={[0]}
        items={[
          {
            classNames: {
              container: "border-none mb-5",
            },
            title: (
              <div className="flex w-full items-center justify-between">
                <RenderCurrencyInText 
                  text={content?.title} 
                  className="text-base font-bold" 
                />
              </div>
            ),
            content: storeDetails && (
              <>
                <div className="mt-md-6 mt-7 flex items-start">
                  <IconLocation className="md fill-primary me-4 h-5 w-5" />
                  <div>
                    <p className="mb-2 text-[0.875rem] font-bold">{storeDetails.name}</p>
                    <p className="text-[0.875rem] font-normal">
                      {getStoreAddressString(storeDetails.address)}
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex items-center">
                  <IconCalendar className="icon md fill-primary me-4 h-5 w-5" />
                  <p className="text-[0.875rem] font-normal">{slotDate}</p>
                </div>
                <div className="mt-4 flex items-center">
                  <IconClock className="icon md me-4" />
                  <p className="text-[0.875rem] font-normal">
                    {convertTimeString(startTime)} - {convertTimeString(endTime)}
                  </p>
                </div>
                <div className="mt-4 flex items-center">
                  <RTAEyeTest className="icon md me-4" />
                  <RenderCurrencyInText 
                    text={serviceTitle} 
                    className="text-[0.875rem] font-normal" 
                  />
                </div>
              </>
            ),
          },
        ]}
      />
      <div>
        <div className="flex flex-col gap-4">
          <Button
            className="w-full text-[0.875rem] font-bold"
            onClick={() => setIsAddToCalendarModalOpen(true)}
          >
            {content?.addToCalendarButton}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-1">
              <Button
                className="w-full border-2 border-black bg-transparent text-[0.875rem] font-bold text-black"
                onClick={() => setIsModalOpen(true)}
                style={{ padding: "1rem 0", height: "3.5rem" }}
              >
                {content?.cancelAppointmentButton}
              </Button>
            </div>
            <div className="col-span-1">
              <CLink
                className="w-full"
                href={`/${locale}/book-eyetest/date?id=${bookingId}&store=${storeDetails?.id}`}
              >
                <Button
                  className="w-full border-2 border-black bg-transparent text-[0.875rem] font-bold text-black"
                  style={{ height: "3.5rem" }}
                >
                  {content?.rescheduleButton}
                </Button>
              </CLink>
            </div>
          </div>
          <div className="text-center">
            <CLink href="/" className="text-[0.875rem] font-normal underline">
              {content?.returnToHomePage}
            </CLink>
          </div>
        </div>
      </div>
      <CancelConfirmationModal
        content={content}
        isOpen={isModalOpen}
        slotDate={slotDate}
        startTime={startTime}
        toggle={() => setIsModalOpen(!isModalOpen)}
        onConfirm={handleCancelBooking}
      />
      <AddToCalendarModal
        isOpen={isAddToCalendarModalOpen}
        toggle={() => setIsAddToCalendarModalOpen(!isAddToCalendarModalOpen)}
        eventDetails={eventDetails}
      />
    </>
  );
};

export default EyeTestSuccessView;
